import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import {Alert, Button, Dialog, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography} from "@mui/material";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import {SurveyInvitation, SurveyInvitationChannel, SurveyInvitationStatus} from "crqlar-react-common/dist/models/survey/surveyInvitation.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {enqueueSnackbar} from "notistack";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import SurveyInvitationStatusChip from "../invitations/SurveyInvitationStatusChip";
import {invitationSurveyLink} from "../shared/surveyPublicLinks.util";
import {useSurveyInvitationRefresh} from "../shared/surveyQueryRefresh";
import SurveyInvitationEmailDialog from "./SurveyInvitationEmailDialog";
import {useSurveyQrLogoUrl} from "./surveyQrBranding.util";
import SurveyQrFullscreenDialog from "./SurveyQrFullscreenDialog";

interface Props {
    invitation: SurveyInvitation
    /**
     * Brand mark for the QR handover. Passed in by callers that know which survey this is, so the
     * poster can carry the survey theme's own logo; an invitation on its own does not carry a theme,
     * so callers that lack it fall back to the organization's mark below rather than to nothing.
     */
    logoUrl?: string
}

const SurveyInvitationLink = ({invitation, logoUrl}: Props) => {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const organizationLogoUrl = useSurveyQrLogoUrl();
    const refreshInvitations = useSurveyInvitationRefresh();
    const [replacement, setReplacement] = useState<SurveyInvitation>();
    const [fullscreen, setFullscreen] = useState(false);
    const [confirmVoid, setConfirmVoid] = useState(false);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [emailing, setEmailing] = useState(false);
    const current = replacement ?? invitation;
    const canVoid = current.status !== SurveyInvitationStatus.COMPLETED && current.status !== SurveyInvitationStatus.VOIDED;
    const canReplace = canVoid && !!current.surveyId && (!!current.guestProfileId || !!current.guestReservationId);
    const link = current.token ? invitationSurveyLink(current.token) : '';
    const language = current.guestLanguage && (getNameForLanguage(current.guestLanguage, true) || current.guestLanguage);

    const voidInvitation = async () => {
        if (busy || readOnly || !canVoid) return;
        setConfirmVoid(false);
        setFullscreen(false);
        setBusy(true);
        setFailed(false);
        try {
            setReplacement(await SurveyService.invitationVoid({id: current.id}));
            if (canReplace) {
                setReplacement(await SurveyService.invitationCreate({
                    surveyId: current.surveyId!,
                    reservationId: current.guestReservationId,
                    profileId: current.guestProfileId
                }));
            }
            enqueueSnackbar(t(canReplace ? 'New invitation link created' : 'Link voided'), {variant: 'success'});
        } catch {
            setFailed(true);
        } finally {
            await refreshInvitations();
            setBusy(false);
        }
    };

    return (
        <Stack gap={2}>
            {failed && <Alert severity={'error'}>{t('This invitation could not be updated.')}</Alert>}
            <SurveyInvitationStatusChip status={current.status} furthestQuestionIndex={current.furthestQuestionIndex}/>
            <TextField value={link} size={'small'} fullWidth InputProps={{readOnly: true, endAdornment: <CopyAdornment value={link}/>}}/>
            <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                {/* One QR affordance: the code is only ever useful turned towards the guest, so it goes
                    straight to the fullscreen handover instead of an inline preview to squint at. */}
                <Button size={'small'} startIcon={<QrCode2Icon/>} disabled={!link} onClick={() => setFullscreen(true)}>
                    {t('QR code')}
                </Button>
                {/* Only a guest's own, still open link can be mailed; the mail goes to their profile's address. */}
                {canVoid && !!current.guestProfileId && (
                    <Button size={'small'} startIcon={<MailOutlineIcon/>} disabled={!link || readOnly || busy} onClick={() => setEmailing(true)}>
                        {t('Send by email')}
                    </Button>
                )}
                {/* Voiding is the rare, destructive step: behind the menu, not beside the everyday ones. */}
                {canVoid && (
                    <>
                        <Tooltip title={t('More actions')}>
                            <IconButton size={'small'} aria-label={t('More actions')} onClick={event => setMenuAnchor(event.currentTarget)}>
                                <MoreVertIcon fontSize={'small'}/>
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                            <MenuItem
                                disabled={readOnly || busy}
                                onClick={() => {
                                    setMenuAnchor(null);
                                    setConfirmVoid(true);
                                }}
                                sx={{color: 'error.main'}}
                            >
                                {t(canReplace ? 'Void link & create new' : 'Void link')}
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Stack>
            {(current.guestName || language) && (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {current.guestName && language
                        ? t('This link is for {{guest}} and opens in {{language}}.', {guest: current.guestName, language})
                        : current.guestName
                            ? t('This link is for {{guest}}.', {guest: current.guestName})
                            : t('This link opens in {{language}}.', {language})}
                </Typography>
            )}
            {current.reservationLabel && <Typography variant={'body2'} color={'text.secondary'}>{current.reservationLabel}</Typography>}
            {current.channel === SurveyInvitationChannel.EMAIL && current.sentAt && (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('Emailed on {{date}}', {date: formatDateDMYHM(current.sentAt)})}
                </Typography>
            )}
            {emailing && (
                <SurveyInvitationEmailDialog
                    invitation={current}
                    onClose={() => setEmailing(false)}
                    onSent={sent => {
                        setReplacement(sent);
                        void refreshInvitations();
                    }}
                />
            )}
            {!!link && <SurveyQrFullscreenDialog open={fullscreen} onClose={() => setFullscreen(false)} value={link} surveyName={current.surveyName} logoUrl={logoUrl ?? organizationLogoUrl}/>}
            <SimpleDialog
                open={confirmVoid}
                onClose={() => setConfirmVoid(false)}
                title={t('Void this link?')}
                message={t(canReplace
                    ? 'The current link stops working and a new one is created for the same guest.'
                    : 'The current link stops working for this guest.')}
                confirmText={t(canReplace ? 'Void & create new' : 'Void link')}
                cancelText={t('Cancel')}
                onConfirm={voidInvitation}
                onCancel={() => setConfirmVoid(false)}
            />
        </Stack>
    );
};

export const SurveyInvitationDialog = ({invitation, logoUrl, open, onClose}: Props & {open: boolean, onClose: () => void}) => {
    const {t} = useTranslation();
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={'sm'}>
            <DialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    {t('Share survey')}
                    <IconButton aria-label={t('Close')} onClick={onClose}><CloseIcon/></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers><SurveyInvitationLink key={invitation.id} invitation={invitation} logoUrl={logoUrl}/></DialogContent>
        </Dialog>
    );
};

export default SurveyInvitationLink;
